import { Component, type ErrorInfo, type ReactNode } from "react";
import { logClientError } from "@/lib/log-client-error";
import {
  clearChunkReloadFlag,
  isStaleChunkError,
  reloadOnceForStaleChunk,
} from "@/lib/lazy-with-retry";

// The app had no error boundary anywhere — any render-time throw (a bad data
// shape, a third-party script conflict, a stale chunk after a deploy) took
// the whole tree down to a blank white page with nothing in the UI and
// nothing actionable for the user. This turns that into a visible, recoverable
// screen instead, and logs the error so it shows up in analytics/console.
interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

// Last-resort net for a stale chunk: lazyWithRetry already retries the import
// and reloads, so anything that still reaches here is a chunk error thrown from
// somewhere it doesn't wrap. Same treatment — see src/lib/lazy-with-retry.ts.

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidMount() {
    // Reached a clean mount with no error — this session's chunks are
    // current, so a future stale-chunk error is worth one more auto-reload.
    if (!this.state.error) {
      clearChunkReloadFlag();
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Unhandled render error:", error, info.componentStack);
    logClientError("react-error-boundary", error, {
      componentStack: info.componentStack ?? undefined,
    });

    if (isStaleChunkError(error)) {
      reloadOnceForStaleChunk();
    }
  }

  render() {
    if (this.state.error) {
      const staleChunk = isStaleChunkError(this.state.error);
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8] px-6">
          <div className="max-w-sm text-center">
            <h1 className="text-lg font-medium text-[#0E1626] mb-2">
              {staleChunk ? "Getting the latest version…" : "Something went wrong"}
            </h1>
            <p className="text-sm text-[#0E1626]/70 font-light mb-5">
              {staleChunk
                ? "This page was updated since you opened it — reloading now. If nothing happens in a few seconds, use the button below."
                : "This page hit an unexpected error. Reloading usually fixes it — if it keeps happening, let us know."}
            </p>
            {/* Always rendered, even on the auto-reload path: if the reload
                call itself is blocked (storage locked down, a beforeunload
                handler, anything else), this is the only way out. */}
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center justify-center rounded-full bg-[#0E1626] text-white px-5 py-2.5 text-sm font-medium hover:opacity-90 transition"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
